import moment, { Moment } from "moment-timezone";

import {
  EVENT_DAYS,
  overlapsEventWindow,
  TZ,
  TShiftScheduleEvent,
} from "./constants";
import { GetVolunteerShiftAvailabilityQuery } from "./graphql/getVolunteerShiftAvailability.generated";
import { GetVolunteerShiftScheduleQuery } from "./graphql/getVolunteerShiftSchedule.generated";
import { TShiftSchedule } from "./types";

// a shift with no max_count gives Number(undefined) which is NaN and ?? does
// not catch it so the NaN used to reach the edit form and get saved back
export const parseMaxCountRestriction = (input: any): number => {
  const maxCount = Number(input?.volunteer?.max_count);
  return Number.isFinite(maxCount) ? maxCount : 0;
};

export const DEFAULT_SHIFT_YEAR = moment.tz(EVENT_DAYS[0], TZ).year();

export const blocksFromTop = (momentStart: Moment) => {
  return moment
    .duration(momentStart.diff(moment.tz(EVENT_DAYS[0], TZ)))
    .asHours();
};

export const yearFromDate = (date: Date | string | null | undefined) => {
  if (date == null || date === "") return null;
  const ms = date instanceof Date ? date.getTime() : Date.parse(String(date));
  if (!Number.isFinite(ms)) return null;
  const year = moment(ms).tz(TZ).year();
  return Number.isFinite(year) ? year : null;
};

export const yearsFromDates = (
  dates: Array<Date | string | null | undefined>
): number[] => {
  const years = new Set<number>([DEFAULT_SHIFT_YEAR]);
  dates.forEach((date) => {
    const year = yearFromDate(date);
    if (year != null) years.add(year);
  });
  return Array.from(years).sort((a, b) => b - a);
};

/** 12:00am–7:00am ET inclusive (7:00 counts; 7:01+ does not). */
export const isOverNight = (time: Moment) => {
  const minutes = time.hour() * 60 + time.minute();
  return minutes >= 0 && minutes <= 7 * 60;
};

/** Counts for the overnight requirement if either end is in the overnight window. */
export const isOvernightShift = (start: Moment, end: Moment) =>
  isOverNight(start) || isOverNight(end);

// ET hours from Friday midnight — same numbers the calendar tiles use
export const eventCanvasPlacement = (start: Date, end: Date) => {
  const momentStart = moment(start).tz(TZ);
  const momentEnd = moment(end).tz(TZ);
  return {
    fromTop: blocksFromTop(momentStart),
    hours: moment.duration(momentEnd.diff(momentStart)).asHours(),
  };
};

export const shiftOverlapsEventDays = (start: Date, end: Date) => {
  const { fromTop, hours } = eventCanvasPlacement(start, end);
  return overlapsEventWindow(fromTop, hours);
};

export const parseAdminShifts = (
  scheduleData: GetVolunteerShiftScheduleQuery,
  availabilityData: GetVolunteerShiftAvailabilityQuery
): TShiftSchedule[] => {
  const signupCountByShiftId = new Map(
    availabilityData.volunteerShiftAvailability.map((availability) => [
      availability.shift_id,
      availability.signup_count,
    ])
  );

  const shifts = scheduleData.volunteerShiftSchedule.flatMap((shift) => {
    if (!shift.start_time || !shift.end_time) return [];

    const signupCount = signupCountByShiftId.get(shift.shift_id) ?? 0;
    const maxVolunteerCount = shift.max_volunteer_count ?? 0;

    return {
      shift: shift.name,
      shift_id: shift.shift_id,
      date: new Date(shift.start_time).toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      }),
      time_start: new Date(shift.start_time),
      time_end: new Date(shift.end_time),
      spots_left: Math.max(maxVolunteerCount - signupCount, 0),
      signup_count: signupCount,
      max_volunteer_count: maxVolunteerCount,
      role: shift.category_name,
      registration_open: shift.registration_open,
    };
  });

  return shifts
    .filter((shift) => shiftOverlapsEventDays(shift.time_start, shift.time_end))
    .sort((a, b) => a.time_start.getTime() - b.time_start.getTime());
};

export type TShiftSection = {
  category: string;
  shifts: TShiftScheduleEvent[];
  lanes: number;
};

// categories the tool already orders by elsewhere; anything else trails alphabetically
const SECTION_ORDER = [
  "Activities",
  "Workshops",
  "Food",
  "Ceremonies",
  "Security",
  "Floor Patrol",
];

const orderCategories = (categories: string[]) =>
  [...categories].sort((a, b) => {
    const ai = SECTION_ORDER.indexOf(a);
    const bi = SECTION_ORDER.indexOf(b);
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.localeCompare(b);
  });

/** Greedy interval packing: non-overlapping shifts share a lane. */
const packSection = (
  category: string,
  shifts: TShiftScheduleEvent[]
): TShiftSection => {
  const sorted = shifts
    .slice()
    .sort((a, b) => a.blocksFromTop - b.blocksFromTop);

  const laneEnds: number[] = [];
  sorted.forEach((shift) => {
    const start = shift.blocksFromTop;
    const end = start + shift.numberOfBlocks;
    let lane = laneEnds.findIndex((busyUntil) => start >= busyUntil);
    if (lane === -1) {
      laneEnds.push(end);
      lane = laneEnds.length - 1;
    } else {
      laneEnds[lane] = end;
    }
    shift.overlapColumn = lane;
  });

  return {
    category,
    shifts: sorted,
    lanes: Math.max(laneEnds.length, 1),
  };
};

/**
 * Groups shifts into one section per category. Within a section a shift only
 * claims its own lane when it genuinely overlaps another shift in that same
 * category, so sequential categories collapse to a single lane.
 *
 * When `collapseToSingleColumn` is set, all shifts share one section (e.g. My
 * shifts only) and cross-category overlaps get separate lanes the same way.
 */
export const groupShiftsByCategory = (
  shifts: TShiftScheduleEvent[],
  options?: { collapseToSingleColumn?: boolean; singleColumnLabel?: string }
): TShiftSection[] => {
  if (options?.collapseToSingleColumn) {
    if (shifts.length === 0) return [];
    return [packSection(options.singleColumnLabel ?? "My shifts", shifts)];
  }

  const byCategory = new Map<string, TShiftScheduleEvent[]>();
  shifts.forEach((shift) => {
    const bucket = byCategory.get(shift.category);
    if (bucket) bucket.push(shift);
    else byCategory.set(shift.category, [shift]);
  });

  return orderCategories(Array.from(byCategory.keys())).map((category) =>
    packSection(category, byCategory.get(category) ?? [])
  );
};

export const sortComputedShifts = (
  computedShifts: TShiftScheduleEvent[]
): void => {
  const columns: {
    start: moment.Moment;
    end: moment.Moment;
    category: string;
  }[][] = [];

  computedShifts.forEach((shift) => {
    let placed = false;

    for (let i = 0; i < columns.length; i++) {
      let fits = true;

      for (let j = 0; j < columns[i].length; j++) {
        const other = columns[i][j];
        if (
          (shift.start.isBefore(other.end) && shift.end.isAfter(other.start)) ||
          shift.category !== other.category
        ) {
          fits = false;
          break;
        }
      }

      if (fits) {
        columns[i].push({
          start: shift.start,
          end: shift.end,
          category: shift.category,
        });
        shift.overlapColumn = i;
        placed = true;
        break;
      }
    }

    if (!placed) {
      columns.push([
        { start: shift.start, end: shift.end, category: shift.category },
      ]);
      shift.overlapColumn = columns.length - 1;
    }
  });
};

/** A shift the volunteer can still take: registration is open, there is a
 *  seat left, and nothing else has locked signup (role, conflict, …). */
export const isShiftAvailable = (
  shift: Pick<TShiftSchedule, "registration_open" | "spots_left">,
  lock?: { disabled?: boolean }
) => !lock?.disabled && shift.registration_open && shift.spots_left > 0;

/** Client-side text filter for schedule list rows (name/date/role). */
export const shiftMatchesSearch = (shift: TShiftSchedule, search: string) => {
  const query = search.trim().toLowerCase();
  if (!query) return true;

  return (
    shift.shift.toLowerCase().includes(query) ||
    shift.date.toLowerCase().includes(query) ||
    shift.role.toLowerCase().includes(query)
  );
};
